import React from 'react';
import { motion } from 'framer-motion';
import { Check, FileText, Handshake, Truck, Shield, Clock, Wrench } from 'lucide-react';

export const Plans = () => {
  const processSteps = [
    {
      icon: <FileText className="h-8 w-8" />,
      title: 'Solicitud',
      description: 'Contáctanos para conocer tus necesidades específicas',
      stat: '1',
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      statColor: 'bg-blue-600'
    },
    {
      icon: <Handshake className="h-8 w-8" />,
      title: 'Cotización',
      description: 'Te presentamos un plan personalizado y competitivo',
      stat: '2',
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      statColor: 'bg-green-600'
    },
    {
      icon: <Check className="h-8 w-8" />,
      title: 'Firma',
      description: 'Contrato flexible adaptado a tu negocio',
      stat: '3',
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      statColor: 'bg-purple-600'
    },
    {
      icon: <Truck className="h-8 w-8" />,
      title: 'Entrega',
      description: 'Instalación y capacitación inmediata',
      stat: '4',
      color: 'orange',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      statColor: 'bg-orange-600'
    },
  ];

  const included = [
    {
      icon: <Shield className="h-6 w-6" />,
      text: 'Instalación y Capacitación personalizada',
      highlight: 'Gratis',
      description: 'Setup completo e instrucción para tu equipo',
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      statColor: 'bg-blue-600'
    },
    {
      icon: <Clock className="h-6 w-6" />,
      text: 'Servicio Técnico en tiempo récord',
      highlight: '24/7',
      description: 'Soporte técnico inmediato cuando lo necesites',
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      statColor: 'bg-green-600'
    },
    {
      icon: <Wrench className="h-6 w-6" />,
      text: 'Refacciones y Consumibles',
      highlight: 'Incluido',
      description: 'Todas las piezas necesarias (excepto papel)',
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      statColor: 'bg-purple-600'
    },
    {
      icon: <Check className="h-6 w-6" />,
      text: 'Tóner Incluido',
      highlight: 'Premium',
      description: 'Cartuchos originales de alta calidad',
      color: 'indigo',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      statColor: 'bg-indigo-600'
    },
    {
      icon: <Shield className="h-6 w-6" />,
      text: 'Cambio de equipo en caso de falla mayor',
      highlight: 'Garantía',
      description: 'Equipo de reemplazo inmediato sin costo',
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      statColor: 'bg-emerald-600'
    },
    {
      icon: <Wrench className="h-6 w-6" />,
      text: 'Mantenimiento preventivo periódico',
      highlight: 'Programado',
      description: 'Revisiones regulares para óptimo rendimiento',
      color: 'orange',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      statColor: 'bg-orange-600'
    },
  ];

  return (
    <section id="planes" className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-semibold text-sm uppercase tracking-wide">
            Planes de Renta
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-3">
            Planes Flexibles para tu Negocio
          </h2>
          <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
            Contrataciones personalizadas con contratos flexibles adaptados a tus necesidades
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12"
          >
            Proceso de Contratación
          </motion.h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                  {/* Step Number Badge */}
                  <div className={`absolute -top-4 -right-4 ${step.statColor} text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-lg`}>
                    {step.stat}
                  </div>

                  <div className={`${step.bgColor} w-16 h-16 rounded-xl flex items-center justify-center ${step.textColor} mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto`}>
                    {step.icon}
                  </div>

                  <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">
                    {step.title}
                  </h4>
                  <p className="text-gray-600 text-center leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* What's Included */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4"
            >
              ¿Qué Incluye la Renta?
            </motion.h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Servicio completo sin sorpresas, todo incluido en tu plan de renta
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {included.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                  {/* Highlight Badge */}
                  <div className={`absolute -top-4 -right-4 ${item.statColor} text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg`}>
                    {item.highlight}
                  </div>
                  
                  {/* Icon */}
                  <div className={`${item.bgColor} w-16 h-16 rounded-xl flex items-center justify-center ${item.textColor} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {item.icon}
                  </div>
                  
                  {/* Content */}
                  <h4 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                    {item.text}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Bottom Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 bg-gradient-to-r from-slate-50 to-gray-100 rounded-2xl p-8 border border-gray-200"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                🎯 Garantía Total de Satisfacción
              </h4>
              <p className="text-gray-600 mb-6 max-w-xl mx-auto">
                Si no estás completamente satisfecho con nuestro servicio, te devolvemos tu dinero sin preguntas
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold text-sm shadow-md">
                  ✅ 30 días de garantía
                </span>
                <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg font-semibold text-sm shadow-md">
                  🔧 Soporte inmediato
                </span>
                <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg font-semibold text-sm shadow-md">
                  📞 Atención 24/7
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Advantages Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20 bg-white rounded-3xl p-8 sm:p-12 shadow-lg border border-gray-100"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Ventajas de Rentar vs Comprar
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Descubre por qué la renta es la mejor opción para tu negocio
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-green-100 rounded-xl border border-emerald-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-lg">$0</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Sin Inversión Inicial</h4>
              <p className="text-gray-600 text-sm">Comienza inmediatamente sin grandes desembolsos de capital</p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-100 rounded-xl border border-blue-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-md">
                <Wrench className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Mantenimiento Incluido</h4>
              <p className="text-gray-600 text-sm">Servicio técnico especializado sin costos adicionales</p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-indigo-100 rounded-xl border border-purple-200 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-md">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Actualización Garantizada</h4>
              <p className="text-gray-600 text-sm">Equipos siempre actualizados con la última tecnología</p>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-700 rounded-3xl p-8 sm:p-12 text-center shadow-2xl relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12"></div>
          
          <div className="relative z-10">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              ¿Listo para tu Plan Personalizado?
            </h3>
            <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
              Obtén una cotización sin compromiso adaptada a las necesidades de tu negocio
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('contacto');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors duration-200 shadow-lg inline-block"
            >
              Solicitar Plan Personalizado
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Plans;
